import React from 'react';
import { observer } from 'mobx-react';
import { useDependencies, provide } from '@servicetitan/react-ioc';
import * as Styles from './application-drawer.less';
import {
    Slide,
    Stepper,
    Step,
    StepLabel,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@material-ui/core';
import { PlanSelectionPage } from './plan-selection-page';
import { DetailsReviewPage } from './details-review-page';
import { ReviewResponse } from './review-response';
import { FinancingLoader } from './financing-loader';
import classnames from 'classnames';
import { getEnumValues } from '@servicetitan/form';
import {
    ServiceFinanceStore,
    ApplicationStage,
    ApplicationStatus,
} from '../../../stores/service-finance.store';
import { ApplicationInformationPage } from './application-information-page/application-information-page';
import { ESignAgreementConsent } from './esign-agreement-consent';
import { ESignAgreementPage } from './esign-agreement-page';
import { SignLoanPage } from './sign-loan-page';
import { FinancingAPIService } from 'src/api/api/financing-bridgeAPI.service';
import { LoansAPIService } from 'src/api/api/loansAPI.service';
import { EstimateStore } from '../../../stores/estimate.store';
import { EstimatesApiService, Estimate } from 'src/api';
import { ClientDataStore } from 'src/core/stores/client-data.store';
import { ClientDataApiService, LoginApiService } from 'src/api';
import { RouterStore } from 'src/core/stores/router.store';

interface ComponentData {
    StepTitle?: string | undefined;
    Component: React.FC;
}

const stageToComponentMap = new Map<ApplicationStage, ComponentData>([
    [
        ApplicationStage.SelectPlan,
        {
            StepTitle: 'Select A Plan',
            Component: PlanSelectionPage,
        },
    ],
    [
        ApplicationStage.AddInfo,
        {
            StepTitle: 'Add Info',
            Component: ApplicationInformationPage,
        },
    ],
    [
        ApplicationStage.ReviewApplication,
        {
            StepTitle: 'Review',
            Component: DetailsReviewPage,
        },
    ],
    [
        ApplicationStage.ReviewResponse,
        {
            Component: ReviewResponse,
        },
    ],
    [
        ApplicationStage.ESignature,
        {
            Component: ESignAgreementPage,
        },
    ],
    [
        ApplicationStage.SignLoan,
        {
            Component: SignLoanPage,
        },
    ],
    [
        ApplicationStage.FinishExternalSignature,
        {
            Component: ReviewResponse,
        },
    ],
    [
        ApplicationStage.Completed,
        {
            Component: ReviewResponse,
        },
    ],
]);

interface ApplicationDrawerProps {
    closeCallback(): void;
    selectedEstimate: Estimate | undefined;
}
export const ApplicationDrawer: React.FC<ApplicationDrawerProps> = provide({
    singletons: [
        ServiceFinanceStore,
        FinancingAPIService,
        LoansAPIService,
        EstimateStore,
        EstimatesApiService,
        ClientDataStore,
        ClientDataApiService,
        LoginApiService,
        RouterStore,
    ],
})(
    observer((props) => {
        const [store] = useDependencies(ServiceFinanceStore);
        const [initialized, setInitialized] = React.useState(false);
        const CurrentPage = stageToComponentMap.get(store.currentStage);
        const steps = getEnumValues(ApplicationStage)
            .map((stage) => stageToComponentMap.get(stage)!.StepTitle)
            .filter((s) => s);
        const currentIndex = steps.findIndex(
            (step) => step === stageToComponentMap.get(store.currentStage)!.StepTitle
        );
        const buttonsData = store.getButtonsData();
        const isESignStage = store.currentStage === ApplicationStage.ESignature;
        React.useEffect(() => {
            store.initialize(props.closeCallback, props.selectedEstimate);
            setInitialized(true);
        }, []);
        return store.applicationIsOpen ? (
            <div onClick={props.closeCallback} className={Styles.container}>
                <Slide direction="left" in={store.applicationIsOpen} mountOnEnter unmountOnExit>
                    <div onClick={store.stopPropagation} className={Styles.drawer}>
                        <div className={Styles.title}>Apply For Financing</div>
                        {CurrentPage && CurrentPage.StepTitle && (
                            <Stepper className={Styles.stepper} activeStep={currentIndex}>
                                {steps.map((title, index) => (
                                    <Step key={index}>
                                        <StepLabel>{title}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        )}
                        {store.isProcessing ? (
                            <FinancingLoader />
                        ) : (
                            CurrentPage && initialized && <CurrentPage.Component />
                        )}

                        <div className={Styles.actionContainer}>
                            {isESignStage && (
                                <ESignAgreementConsent
                                    consents={store.eSignConsentAgreement}
                                    hasCoApplicant={store.applicationForm!.$.hasCoApplicant.value}
                                />
                            )}
                            <div className={Styles.flexBox}>
                                {store.currentStage !== ApplicationStage.Completed && (
                                    <div
                                        onClick={props.closeCallback}
                                        className={Styles.cancelButton}
                                    >
                                        {buttonsData.cancelTitle}
                                    </div>
                                )}
                                <Button
                                    className={Styles.nextButton}
                                    disabled={store.isProcessing || !store.isNextButtonEnabled}
                                    onClick={buttonsData.action}
                                    color="primary"
                                >
                                    {buttonsData.nextTitle}
                                </Button>
                            </div>
                            {buttonsData.miscTitle && (
                                <div className={Styles.miscButton} onClick={buttonsData.miscAction}>
                                    {buttonsData.miscTitle}
                                </div>
                            )}
                        </div>
                        {store.dialogData && (
                            <Dialog open={store.isDialogOpen} onClose={store.closeDialog}>
                                <DialogTitle>{store.dialogData.title}</DialogTitle>
                                <DialogContent>
                                    <DialogContentText id="alert-dialog-description">
                                        {store.dialogData.message}
                                    </DialogContentText>
                                </DialogContent>
                                <DialogActions>
                                    <Button
                                        className={Styles.secondaryButton}
                                        onClick={store.dialogData.secondaryAction}
                                    >
                                        No
                                    </Button>
                                    <Button
                                        onClick={store.dialogData.primaryAction}
                                        color="primary"
                                    >
                                        Yes
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        )}
                    </div>
                </Slide>
            </div>
        ) : null;
    })
);
